import { useState, useEffect, useRef, useCallback } from "react";
import { CSS } from "./styles";
// PREMIUM-PART-1-APPLIED + PREMIUM-PART-2-APPLIED + PREMIUM-PART-3-APPLIED
import "./styles/tactile.css";
import "./styles/animations.css";
import { Splash } from "./components/Splash";
import { LoginPage } from "./components/LoginPage";
import { ModePage } from "./components/ModePage";
// GA-ROUTE-TRACKING-APPLIED: fire page_view on every state-route change + tag events with Firebase UID.
import { trackPage, setUserId } from "./analytics";
// TUTORIAL-MODE-APPLIED: pre-signup sneak peek concept shown to unauthenticated visitors.
import { TutorialMode } from "./components/TutorialMode";
import { ExplainPage } from "./components/ExplainPage";
import { TestApp } from "./components/TestApp";
import { TutorApp } from "./components/TutorApp";
import { Dashboard } from "./components/Dashboard";
import { FlashCardsPage } from "./components/FlashCardsPage";
import { ExamCornerPage } from "./components/ExamCornerPage";
// COMPETENCY-FULLPAGE-APPLIED: Competency Building is a dedicated full-page route.
import { CompetencyPage } from "./components/CompetencyTab";
import { BitesPage } from "./components/BitesPage";
import { SettingsPage } from "./components/SettingsPage";
import { ProPage } from "./components/ProPage";
import { OnboardingSlides, shouldShowOnboarding } from "./components/OnboardingSlides";
import { trackDailyActivity } from "./progress";
// PROGRESS-SYNC-APPLIED: cross-device progress mirroring via Firestore.
import { syncFromFirestore, setSyncUid, startRealtimeSync } from "./progress-sync";
import { API_BASE } from "./constants/colors";
import { authedFetch } from "./firebase";
import soundManager from "./sounds/sound-manager";
import { onAuthChange, getUserProfile, logOut, updateUserProfile, handleRedirectSignIn } from "./firebase";

// TUTORIAL-REPLAY-UIDS: authenticated users in this list always see the tutorial from splash
// instead of going to home. Useful for founder/QA replay. To add UIDs just append below.
const TUTORIAL_REPLAY_UIDS = ["gKbVQKQAFwgdgCpN51F7iGDDHo93"];

export default function App() {
  const [page, setPage] = useState("splash");
  const [mode, setMode] = useState("homework");
  const [lang, setLang] = useState("en");
  const [profile, setProfile] = useState(null);
  const [curriculumData, setCurriculumData] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  // PROGRESS-SYNC-APPLIED: true while we're pulling Firestore progress on sign-in so
  // ModePage's hero can show a skeleton instead of flashing "Start your journey".
  const [progressSyncing, setProgressSyncing] = useState(false);
  // CROSS-DEVICE-FIX-APPLIED: bumped whenever the realtime listener merges in
  // progress from another device, forcing a re-read of localStorage-backed stats.
  const [, setProgressVersion] = useState(0);
  const refreshProgress = useCallback(() => setProgressVersion((v) => v + 1), []);
  // Arm full cross-device sync for a uid: pull + merge once, enable debounced
  // merge-writes, and subscribe to live changes from other devices.
  const armSync = useCallback(async (uid) => {
    if (!uid) return;
    setProgressSyncing(true);
    try {
      await syncFromFirestore(uid);
      setSyncUid(uid);
      startRealtimeSync(uid, refreshProgress);
    } catch (e) {
      console.warn("Progress sync failed:", e);
    }
    setProgressSyncing(false);
  }, [refreshProgress]);
  const [exitToast, setExitToast] = useState(false);
  // REVISE-EXAM-STEP-3-APPLIED: subject passed from ModePage Exam Corner chip selection
  const [examSubject, setExamSubject] = useState(null);
  // TEST-FLOW-BACK-NAV-APPLIED: where ModePage should land when user returns (e.g. back from Exam Corner → Revise tab)
  const [modeReturnTo, setModeReturnTo] = useState(null);
  // Subject preselected from ModePage's subject tile — lets ExplainPage skip the subject picker
  const [explainInitialSubject, setExplainInitialSubject] = useState(null);
  // Chapter preselected by Resume tap — lets ExplainPage jump straight into that chapter's topic page
  const [explainInitialChapter, setExplainInitialChapter] = useState(null);
  // TUTORIAL-MODE-APPLIED: when post-tutorial signup/signin tap routes to LoginPage,
  // this controls whether it opens on the "login" or "signup" tab.
  const [loginInitialMode, setLoginInitialMode] = useState("login");
  const lastBackPress = useRef(0);

  // Load curriculum JSON on mount
  useEffect(() => {
    fetch("/data/curriculum.json")
      .then(r => r.json())
      .then(data => setCurriculumData(data))
      .catch(err => console.warn("Could not load curriculum:", err));
  }, []);

  // Preload UI sounds once on boot. Idempotent inside sound-manager, so hot
  // reloads / StrictMode double-invokes are harmless. Missing files are
  // silently tolerated at play time.
  useEffect(() => {
    soundManager.preload({
      tap: "/sounds/tap.wav",
      option_select: "/sounds/option-select.wav",
      correct: "/sounds/correct.wav",
      wrong: "/sounds/wrong.wav",
      topic_complete: "/sounds/topic-complete.wav",
      chapter_complete: "/sounds/chapter-complete.wav",
    });
  }, []);

  // Handle redirect sign-in result (for Capacitor / mobile)
  useEffect(() => {
    handleRedirectSignIn().then(async (user) => {
      if (user) {
        try {
          const p = await getUserProfile(user.uid);
          if (p) {
            // CLASS-10-ONLY-BETA-APPLIED: force-migrate non-Class-10 users.
            // Beta only ships content for Class 10, so any existing user on
            // 6/7/8/9 is auto-promoted to Class 10 in Firestore + state.
            const needsMigration = p.studentClass && p.studentClass !== 10;
            if (needsMigration) {
              try { await updateUserProfile(user.uid, { studentClass: 10 }); } catch (e) { console.warn("Class-10 migration write failed:", e); }
            }
            const prof = {
              uid: user.uid,
              name: p.name || user.displayName || "Student",
              email: user.email,
              studentClass: 10, // beta lock
              board: p.board || "CBSE",
              lang: p.lang || "en",
            };
            setProfile(prof);
            // GA-ROUTE-TRACKING-APPLIED: tag subsequent events with Firebase UID so GA's
            // User Explorer can replay any single student's session. UID only — never email/name.
            setUserId(prof.uid);

            if (p.lang) setLang(p.lang);
            setPage(shouldShowOnboarding() ? "onboarding" : "mode");
          } else {
            // New Google user — send to login to pick class/board
            setPage("login");
          }
        } catch (e) {
          console.warn("Redirect profile load failed:", e);
        }
      }
    });
  }, []);

  // Listen for auth state — load profile but DON'T skip splash
  useEffect(() => {
    const unsubscribe = onAuthChange(async (user) => {
      if (user) {
        try {
          const p = await getUserProfile(user.uid);
          if (p) {
            // CLASS-10-ONLY-BETA-APPLIED: same migration path as the redirect handler.
            const needsMigration = p.studentClass && p.studentClass !== 10;
            if (needsMigration) {
              try { await updateUserProfile(user.uid, { studentClass: 10 }); } catch (e) { console.warn("Class-10 migration write failed:", e); }
            }
            const prof = {
              uid: user.uid,
              name: p.name || user.displayName || "Student",
              email: user.email,
              studentClass: 10, // beta lock
              board: p.board || "CBSE",
              lang: p.lang || "en",
            };
            setProfile(prof);
            // GA-ROUTE-TRACKING-APPLIED: same as the redirect handler.
            setUserId(prof.uid);

            if (p.lang) setLang(p.lang);

            // CROSS-DEVICE-FIX-APPLIED: pull + merge, arm debounced merge-writes, and
            // start the realtime listener. Hero shows a skeleton while this resolves.
            await armSync(user.uid);
          }
        } catch (e) {
          console.warn("Failed to load profile:", e);
        }
      } else {
        // PROGRESS-SYNC-APPLIED: signed out — stop scheduling Firestore writes.
        setSyncUid(null);
      }
      setAuthChecked(true);
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After splash, route based on auth.
  // TUTORIAL-MODE-APPLIED: unauthenticated users see the 5-slide tutorial before signup;
  // authenticated users go to home — EXCEPT for allowlisted UIDs (founder/QA), who always
  // see the tutorial from splash for replay testing.
  const handleSplashDone = () => {
    if (profile) {
      if (TUTORIAL_REPLAY_UIDS.includes(profile.uid)) {
        setPage("tutorial");
        return;
      }
      trackDailyActivity();
      if (shouldShowOnboarding()) {
        setPage("onboarding");
      } else {
        setPage("mode");
      }
    } else {
      setPage("tutorial");
    }
  };

  const handleLogin = (p) => {
    setProfile(p);
    if (p.lang) setLang(p.lang);
    // CROSS-DEVICE-FIX-APPLIED: arm sync immediately for fresh sign-ups, whose
    // profile doc may not have existed when onAuthChange first fired.
    if (p?.uid) armSync(p.uid);
    if (shouldShowOnboarding()) {
      setPage("onboarding");
    } else {
      setPage("mode");
    }
  };

  const handleLogout = async () => {
    await logOut();
    setProfile(null);
    setPage("splash");
  };

  // No separate loading screen — splash IS the loading screen
  // Auth check happens in background during splash

  // Android back button handling — uses browser history API (works in all WebViews)
  useEffect(() => {
    // Push initial history entry
    window.history.pushState({ page: "mode" }, "");

    const handler = (e) => {
      const currentPage = document.querySelector("[data-page]")?.dataset.page;
      
      if (currentPage === "mode" || currentPage === "splash" || currentPage === "login") {
        // On home/splash/login — double press to exit
        const now = Date.now();
        if (now - lastBackPress.current < 2000) {
          // Exit app if Capacitor available
          if (window.Capacitor?.Plugins?.App?.exitApp) {
            window.Capacitor.Plugins.App.exitApp();
          }
          return; // Let browser handle if not Capacitor
        } else {
          // First press — show toast, push history to prevent actual back
          lastBackPress.current = now;
          setExitToast(true);
          setTimeout(() => setExitToast(false), 2000);
          window.history.pushState({ page: currentPage }, "");
        }
      } else {
        // On any other page — try slide-level back first, then go home
        if (window.__samajhGoBack) {
          window.__samajhGoBack();
        } else {
          window.__samajhGoHome?.();
        }
        window.history.pushState({ page: "inner" }, "");
      }
    };

    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);

  // Push history entry on every page change so back button works
  useEffect(() => {
    window.history.pushState({ page }, "");
  }, [page]);

  // BACK-AUTH-FIX: keep a live ref of the auth state so the handlers below (registered
  // once with []-deps) read the CURRENT profile, not a stale closure value.
  const profileRef = useRef(profile);
  useEffect(() => { profileRef.current = profile; }, [profile]);

  // Expose goHome for back button handler. AUTH-AWARE: a signed-out user must never land
  // on "mode" (the home page) — back from a pre-signup screen sends them to splash.
  useEffect(() => {
    window.__samajhGoHome = () => setPage(profileRef.current ? "mode" : "splash");
    return () => { delete window.__samajhGoHome; };
  }, []);

  // CAPACITOR-BACK-BUTTON-APPLIED: the popstate/history approach above is unreliable
  // inside the Capacitor Android WebView — the hardware back button doesn't consistently
  // emit `popstate`, so Android falls through to finishing the activity (closing the app
  // on every press). Registering an explicit `backButton` listener DISABLES that default
  // and gives us full control: in-app navigation on inner pages, double-press-to-exit on
  // the home screen. No-ops on web (no Capacitor) — the popstate handler covers browsers.
  useEffect(() => {
    const CapApp = window.Capacitor?.Plugins?.App;
    if (!CapApp?.addListener) return;
    let handle;
    const onBack = () => {
      const currentPage = document.querySelector("[data-page]")?.dataset.page;
      const topLevel = currentPage === "mode" || currentPage === "splash" || currentPage === "login";
      if (!topLevel) {
        // Inner page: try slide-level back first (learn flow), else go home.
        // AUTH-AWARE: signed-out users go to splash, never the home page.
        if (window.__samajhGoBack) window.__samajhGoBack();
        else if (window.__samajhGoHome) window.__samajhGoHome();
        else setPage(profileRef.current ? "mode" : "splash");
        return;
      }
      // Home/splash/login: require a second press within 2s to actually exit.
      const now = Date.now();
      if (now - lastBackPress.current < 2000) {
        CapApp.exitApp?.();
      } else {
        lastBackPress.current = now;
        setExitToast(true);
        setTimeout(() => setExitToast(false), 2000);
      }
    };
    // WHITE-SCREEN-FIX: wrap in try/catch and handle both Promise and plain-handle
    // returns. Previously `.then()` was chained directly; if addListener didn't return
    // a thenable on-device, the throw crashed the whole React tree (blank/white screen).
    try {
      const reg = CapApp.addListener("backButton", onBack);
      if (reg && typeof reg.then === "function") reg.then((h) => { handle = h; }).catch(() => {});
      else handle = reg;
    } catch (e) {
      console.warn("[back-button] listener setup failed:", e);
    }
    return () => { try { handle?.remove?.(); } catch {} };
  }, []);

  // GA-ROUTE-TRACKING-APPLIED: state-based routing means the URL never changes,
  // so GA's auto page_view never fires. This effect fires one manually on each
  // page transition so GA sees the full /splash → /login → /mode → /explain → ...
  // funnel. The very first render also triggers this, replacing the auto page_view
  // that we disabled in public/index.html (send_page_view: false).
  useEffect(() => {
    trackPage(page);
  }, [page]);

  return (
    <div data-page={page}>
      <style>{CSS}</style>
      {/* Exit toast */}
      {exitToast && (
        <div style={{
          position: "fixed", bottom: 80, left: "50%", transform: "translateX(-50%)",
          background: "rgba(0,0,0,0.8)", color: "white", padding: "10px 24px",
          borderRadius: 24, fontSize: 13, zIndex: 9999, whiteSpace: "nowrap",
        }}>
          Press again to exit
        </div>
      )}
      {page==="splash"  && <Splash onStart={handleSplashDone} lang={lang} setLang={setLang}/>}
      {/* TUTORIAL-MODE-APPLIED: pre-signup sneak peek. onSignUp/onSignIn route to LoginPage in the right tab.
          isAuthenticated lets TutorialMode skip the end-of-tutorial signup modal for allowlisted UIDs
          (they're already signed in — no point asking them to sign up).
          onExit destination: authenticated users go to mode (home); unauthenticated back to splash. */}
      {page==="tutorial" && (
        <TutorialMode
          lang={lang}
          isAuthenticated={!!profile}
          onExit={() => setPage(profile ? "mode" : "splash")}
          onSignUp={() => { setLoginInitialMode("signup"); setPage("login"); }}
          onSignIn={() => { setLoginInitialMode("login"); setPage("login"); }}
        />
      )}
      {page==="login"   && <LoginPage lang={lang} setLang={setLang} onLogin={handleLogin} initialMode={loginInitialMode}/>}
      {page==="onboarding" && <OnboardingSlides lang={lang} setLang={setLang} onDone={() => setPage("mode")} />}
      {page==="mode"    && <ModePage profile={profile} lang={lang} setLang={setLang} curriculumData={curriculumData} progressSyncing={progressSyncing} initialTab={modeReturnTo?.tab} initialReviseView={modeReturnTo?.reviseView} onModeConsumed={()=>setModeReturnTo(null)} onMode={(m, subj)=>{setMode(m);setExplainInitialSubject(subj || null);setExplainInitialChapter(null);setPage(m==="explain"?"explain":m==="test"?"explain":"app");}} onResumeChapter={(subj, ch)=>{setMode("explain");setExplainInitialSubject(subj || null);setExplainInitialChapter(ch || null);setPage("explain");}} onDashboard={()=>setPage("dashboard")} onFlashCards={()=>setPage("flashcards")} onExamCorner={(subj)=>{ setExamSubject(subj||null); setPage("examcorner"); }} onCompetency={()=>setPage("competency")} onBites={()=>setPage("bites")} onSettings={()=>setPage("settings")} onUpgrade={()=>setPage("pro")} onClassChange={async (cls) => {
        // CLASS-PICKER-FIX-APPLIED: update React state + persist to Firebase (source of truth)
        // CLASS-10-ONLY-BETA-APPLIED: silently ignore class changes other than 10 (UI is locked too).
        if (cls !== 10) return;
        setProfile(p => ({ ...p, studentClass: cls }));
        if (profile?.uid) { try { await updateUserProfile(profile.uid, { studentClass: cls }); } catch(e) {} }
      }}/> }
      {page==="settings" && <SettingsPage profile={profile} lang={lang} setLang={setLang} onBack={()=>setPage("mode")} onLogout={handleLogout} onUpgrade={()=>setPage("pro")} onClassChange={async (cls) => {
        // CLASS-10-ONLY-BETA-APPLIED: silently ignore class changes other than 10 (UI is locked too).
        if (cls !== 10) return;
        setProfile(p => ({ ...p, studentClass: cls }));
        if (profile?.uid) { try { await updateUserProfile(profile.uid, { studentClass: cls }); } catch(e) {} }
      }} onBoardChange={async (board) => {
        setProfile(p => ({ ...p, board }));
        if (profile?.uid) { try { await updateUserProfile(profile.uid, { board }); } catch(e) {} }
      }}/> }
      {page==="pro" && <ProPage lang={lang} onClose={()=>setPage("settings")} onSubscribe={(tier) => { /* Payment integration later — for now code entry handles it */ setPage("settings"); }} />}
      {/* ExplainPage stays mounted (hidden) when chat opens from board questions */}
      {(page==="explain" || (page==="app" && profile?.cameFromExplain)) && (
        <div style={{display: page==="explain" ? "block" : "none"}}>
          <ExplainPage profile={profile} lang={lang} setLang={setLang} mode={mode} curriculumData={curriculumData} initialSubject={explainInitialSubject} initialChapter={explainInitialChapter} onHome={()=>{setExplainInitialSubject(null);setExplainInitialChapter(null);setPage("mode");}} onStartPractice={async (topic,subject,chapterName)=>{if(mode==="test"){setProfile(p=>({...p,explainTopic:topic,explainSubject:subject,explainChapter:chapterName}));setPage("test");return;}
        // If topic looks like a complete question (from board questions), use it directly
        const isDirectQuestion = topic.includes("?") || topic.length > 60;
        if (isDirectQuestion) {
          setProfile(p=>({...p,explainTopic:topic,explainSubject:subject,explainChapter:chapterName,cameFromExplain:true}));
          setPage("app");
          setTimeout(()=>{ window.__samajhAutoStart?.(topic); }, 500);
          return;
        }
        try {
          const res = await authedFetch(`${API_BASE}/api/next-question`, {
            method:"POST",
            body: JSON.stringify({
              problem:`Generate a practice question on "${topic}" (${subject}) for Class ${profile?.studentClass||10} ${profile?.board||"CBSE"}. Make it self-contained with all values needed.`,
              studentClass: profile?.studentClass||10,
              board: profile?.board||"CBSE",
              lang,
            }),
          });
          const data = await res.json();
          const question = data.content?.[0]?.text || topic;
          setProfile(p=>({...p,explainTopic:topic,explainSubject:subject,explainChapter:chapterName,cameFromExplain:true}));
          setPage("app");
          setTimeout(()=>{
            window.__samajhAutoStart?.(question);
          }, 500);
        } catch(e) {
          setProfile(p=>({...p,explainTopic:topic,explainSubject:subject,explainChapter:chapterName,cameFromExplain:true}));
          setPage("app");
        }
      }}/>
        </div>
      )}
      {page==="app"     && <TutorApp lang={lang} setLang={setLang} onHome={()=>{
        if(profile?.cameFromExplain){setProfile(p=>({...p,cameFromExplain:false}));setPage("explain");}
        else{setPage("mode");}
      }} profile={profile}/>}
      {page==="test"    && <TestApp lang={lang} setLang={setLang} profile={profile} onHome={()=>setPage("mode")} topic={profile?.explainTopic||""} subject={profile?.explainSubject||"Science"} chapterName={profile?.explainChapter||""} curriculumData={curriculumData}/>}
      {page==="dashboard" && <Dashboard profile={profile} lang={lang} curriculumData={curriculumData} onBack={()=>setPage("mode")} onChapter={(subj, ch)=>{
        setProfile(p=>({...p, explainSubject:subj, explainChapter:ch}));
        setMode("explain");
        setPage("explain");
      }}/>}
      {page==="flashcards" && <FlashCardsPage profile={profile} lang={lang} onBack={()=>setPage("mode")} />}
      {page==="examcorner" && <ExamCornerPage profile={profile} lang={lang} initialSubject={examSubject} onBack={()=>{setExamSubject(null); setModeReturnTo({ tab: "revise", reviseView: "exam" }); setPage("mode");}} onChat={(question, subject, chapter)=>{
        setProfile(p=>({...p, explainTopic:question, explainSubject:subject, explainChapter:chapter}));
        setPage("app");
        setTimeout(()=>{ window.__samajhAutoStart?.(question); }, 500);
      }} />}
      {page==="competency" && <CompetencyPage cls={profile?.studentClass || 10} lang={lang} curriculumData={curriculumData} onBack={()=>setPage("mode")} />}
      {page==="bites" && <BitesPage cls={profile?.studentClass || 10} lang={lang} onBack={()=>setPage("mode")} />}
    </div>
  );
}
